/* eslint-disable no-await-in-loop, react-hooks/exhaustive-deps */
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Iconify } from 'src/components';
import TemplateFilterToolbar from 'src/components/templates/TemplateFilterToolbar';
import TemplatePreviewDrawer from 'src/components/templates/TemplatePreviewDrawer';
import { APPLE } from 'src/components/templates/appleDesignTokens';
import RequestTemplateDialog from 'src/components/templates/RequestTemplateDialog';
import WhatsAppAudienceRulesEditor from 'src/components/whatsappComponents/WhatsAppAudienceRulesEditor';
import { audienceSummary, audienceDescription, effectiveRule, treatmentNameIndex, toRuleValue, toPayload, validateRule, AUDIENCE_POLICY } from 'src/components/whatsappComponents/audienceRulesUtils';
import useAudiencePermission from 'src/components/whatsappComponents/useAudiencePermission';
import { dispatch, useSelector } from '../../../redux/store';
import { getPracticeId } from '../../../redux/slices/clincheck';
import { getSearchWpTemplateList, updateWpContextMap, removeTemplateById, bulkUpdateUsageType, revertUsageTypes, updateSingleUsageType } from '../../../redux/slices/whatsAppMessages';
import { getWhatsappAccount } from '../../../redux/slices/whatsappAccount';
import { getPracticeTreatmentList } from '../../../redux/slices/practiceTreatment';
import { updateError } from '../../../redux/slices/user';
import { callApi } from '../../../utils/apiCall';
import {
  getFirstConnectedWhatsappBusinessAccount,
  isConnectedWhatsappBusinessAccount,
  normalizeWhatsappBusinessAccounts,
} from '../../../utils/whatsappAccountLifecycle';

// Mirrors backend ``apps.commons.choices.TEMPLATE_USAGE_TYPE_CHOICES``.
// ``FORMS`` is the discriminator the Send Forms wizard filters on — admins
// must be able to retag an existing approved template to FORMS without
// re-creating it, so the inline dropdown on this list view exposes it
// alongside the create-dialog dropdown in ``RequestTemplateDialog.jsx``.
const DEFAULT_USAGE_TYPES = ['MANUAL', 'AUTOMATION', 'NOTIFICATION', 'TRANSACTIONAL', 'REMINDER', 'FOLLOW_UP', 'COMMUNICATION', 'MARKETING', 'PROMOTION', 'NEWSLETTER', 'FORMS', 'OTHER'];

const formatUsageLabel = (val) => {
  if (!val) return '';
  return val.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const WhatsappTemplateList = ({ initialSearch = '' }) => {
  const theme = useTheme();
  const practiceId = getPracticeId();
  const { whatsappAccountData } = useSelector((state) => state.whatsappAccount);
  const { wpTemplateListGlobal, loadingForWpTemplate } = useSelector((state) => state.whatsappMessages);
  const usageTypeEnum = useSelector((state) => state.schema?.usageType || []);
  // Audience rules are opt-in per practice. Off => the column, the filter,
  // the bulk action and the drawer section are all absent, so the screen is
  // byte-identical to what it was before the feature existed.
  const { canView: canViewAudience, canManage: canManageAudience } = useAudiencePermission();
  const usageTypes = usageTypeEnum?.length > 0 ? usageTypeEnum : DEFAULT_USAGE_TYPES;

  // Seeded once from the URL (`?q=`) by TemplatesPage; local state afterwards.
  const [search, setSearch] = useState(initialSearch);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterUsageType, setFilterUsageType] = useState('');

  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [bulkUsageType, setBulkUsageType] = useState('');
  // Audience: one filter value ('' | 'restricted' | 'everyone' | 'inherit'),
  // plus the bulk-edit dialog draft. The draft starts on CUSTOM because
  // opening the dialog is itself the intent to set an explicit rule; the
  // Practice-default option inside it is the reset path.
  const [filterAudience, setFilterAudience] = useState('');
  const [bulkAudienceOpen, setBulkAudienceOpen] = useState(false);
  const [bulkAudienceDraft, setBulkAudienceDraft] = useState(
    () => toRuleValue({ audience_policy: AUDIENCE_POLICY.CUSTOM }),
  );
  const [bulkAudienceSaving, setBulkAudienceSaving] = useState(false);
  const [bulkPatching, setBulkPatching] = useState(false);
  const [patchingId, setPatchingId] = useState(null);

  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [creatingTemplate, setCreatingTemplate] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [cloneData, setCloneData] = useState(null);
  const [pendingPollCount, setPendingPollCount] = useState(0);

  const accountRows = normalizeWhatsappBusinessAccounts(whatsappAccountData);
  const selectedAccount = getFirstConnectedWhatsappBusinessAccount(accountRows) || accountRows[0] || null;
  const wpAccountId = selectedAccount?.id;
  const canWriteProvider = isConnectedWhatsappBusinessAccount(selectedAccount);

  // ── Listen for WebSocket template status notifications ──
  const { emailNotifications } = useSelector((state) => state.liveNotifications);
  const lastSeenNotifIdRef = useRef(null);

  useEffect(() => {
    if (!emailNotifications?.length || !wpAccountId) return;
    const latest = emailNotifications[emailNotifications.length - 1];
    if (!latest || latest.id === lastSeenNotifIdRef.current) return;
    if (latest.verb === 'whatsapp_template_status') {
      lastSeenNotifIdRef.current = latest.id;
      dispatch(getSearchWpTemplateList({ wpAccountId }));
      const tplName = latest.data?.template_name || latest.data?.name || 'Template';
      const tplStatus = (latest.data?.status || '').toUpperCase();
      if (tplStatus === 'APPROVED') {
        dispatch(updateError({ message: `Template "${tplName}" is now approved`, success: true }));
      } else if (tplStatus === 'REJECTED') {
        dispatch(updateError({ message: `Template "${tplName}" was rejected${latest.data?.rejected_reason ? `: ${latest.data.rejected_reason}` : ''}`, success: false }));
      }
      setPendingPollCount(0);
    }
  }, [emailNotifications, wpAccountId]);

  // ── Polling fallback after create/edit ──
  useEffect(() => {
    if (pendingPollCount <= 0 || !wpAccountId) return;
    const timer = setTimeout(() => {
      dispatch(getSearchWpTemplateList({ wpAccountId }));
      setPendingPollCount((prev) => prev - 1);
    }, 30000);
    return () => clearTimeout(timer);
  }, [pendingPollCount, wpAccountId]);

  // Treatment catalogue for the audience whitelist/blacklist picker. Same
  // 500-row limit the engagement-template targeting picker uses.
  const practiceTreatments = useSelector((state) => state.practiceTreatment?.treatmentsList?.results) || [];
  const treatmentNames = useMemo(() => treatmentNameIndex(practiceTreatments), [practiceTreatments]);
  useEffect(() => {
    if (practiceId && canManageAudience) {
      dispatch(getPracticeTreatmentList(practiceId, { limit: 500, offset: 0 }));
    }
  }, [practiceId, canManageAudience]);

  const templates = useMemo(() => (Array.isArray(wpTemplateListGlobal) ? wpTemplateListGlobal : []), [wpTemplateListGlobal]);
  const existingNames = useMemo(() => templates.map((t) => t.name), [templates]);

  // Clear bulk usage type when all rows are deselected
  useEffect(() => {
    if (selectedRowIds.length === 0) {
      setBulkUsageType('');
    }
  }, [selectedRowIds]);

  const handleDeleteTemplate = useCallback(async (row) => {
    if (!canWriteProvider || !wpAccountId || !row?.id) return;
    setDeletingId(row.id);
    try {
      const res = await callApi(
        `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${wpAccountId}/whatsapp-message-templates/${row.id}/delete_from_meta/`,
        'POST'
      );
      if (res.ok) {
        dispatch(removeTemplateById(row.id));
        dispatch(updateError({ message: `Template "${row.name}" deleted`, success: true }));
      } else {
        dispatch(updateError({ message: res.data?.detail || 'Failed to delete template', success: false }));
      }
    } catch {
      dispatch(updateError({ message: 'Failed to delete template', success: false }));
    } finally {
      setDeletingId(null);
    }
  }, [canWriteProvider, wpAccountId, practiceId]);

  const handleCloneTemplate = useCallback((row) => {
    if (!canWriteProvider) return;
    const header = row.components?.find(c => c.type === 'HEADER');
    const body = row.components?.find(c => c.type === 'BODY');
    const footer = row.components?.find(c => c.type === 'FOOTER');
    const buttons = row.components?.find(c => c.type === 'BUTTONS');
    const btn = buttons?.buttons?.[0];

    let buttonType = 'none';
    let buttonLabel = 'Book Now';
    let buttonUrl = '';
    let buttonPhone = '';
    if (btn?.type === 'URL') { buttonType = 'url'; buttonLabel = btn.text || 'Book Now'; buttonUrl = btn.url || ''; }
    else if (btn?.type === 'PHONE_NUMBER') { buttonType = 'phone'; buttonLabel = btn.text || 'Call Us'; buttonPhone = btn.phone_number || ''; }

    // Set as a NEW template (not edit) but pre-fill with cloned content
    setEditingTemplate(null); // ensure it's not in edit mode
    setCloneData({
      displayName: `${(row.name || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Copy`,
      headerFormat: header?.format || 'TEXT',
      headerText: header?.text || '',
      bodyText: body?.text || '',
      footerText: footer?.text || '',
      buttonType,
      buttonLabel,
      buttonUrl,
      buttonPhone,
      usageType: row.usage_type || 'FOLLOW_UP',
      category: row.category || 'UTILITY',
    });
    setRequestDialogOpen(true);
  }, [canWriteProvider]);

  const handleSyncTemplates = useCallback(async () => {
    if (!canWriteProvider || !wpAccountId || syncing) return;
    setSyncing(true);
    dispatch(updateError({ message: 'Template Sync In Progress!', success: 'warning' }));
    try {
      await callApi(
        `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${wpAccountId}/sync_templates/`,
        'POST'
      );
      dispatch(getSearchWpTemplateList({ wpAccountId }));
      dispatch(updateError({ message: 'Successfully synced the templates!', success: true }));
    } catch {
      dispatch(updateError({ message: 'Failed to sync templates!', success: false }));
    } finally {
      setSyncing(false);
    }
  }, [canWriteProvider, wpAccountId, practiceId, syncing]);

  const handleCreateTemplate = useCallback(async (payload) => {
    if (!canWriteProvider || !wpAccountId) return;
    setCreatingTemplate(true);
    try {
      const res = await callApi(
        `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${wpAccountId}/whatsapp-message-templates/create_from_meta/`,
        'POST',
        payload
      );
      if (res.ok) {
        const category = res.data?.category || '';
        const tplName = res.data?.template_name || '';
        dispatch(updateError({
          message: `Template "${tplName}" created as ${category} — pending Meta approval. You'll be notified when Meta approves it (usually 1-3 minutes).`,
          success: true,
        }));
        setRequestDialogOpen(false);
        dispatch(getSearchWpTemplateList({ wpAccountId }));
        setPendingPollCount(6);
      } else {
        const errMsg = res.data?.detail || res.data?.error || 'Failed to create template';
        dispatch(updateError({ message: typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg), success: false }));
      }
    } catch {
      dispatch(updateError({ message: 'Failed to create template', success: false }));
    } finally {
      setCreatingTemplate(false);
    }
  }, [canWriteProvider, wpAccountId, practiceId]);

  const handleEditTemplate = useCallback(async (payload) => {
    if (!canWriteProvider || !wpAccountId || !payload.templateId) return;
    setCreatingTemplate(true);
    try {
      const res = await callApi(
        `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${wpAccountId}/whatsapp-message-templates/${payload.templateId}/update_from_meta/`,
        'POST',
        { template: payload.template, components: payload.components }
      );
      if (res.ok) {
        dispatch(updateError({
          message: 'Template updated and submitted to Meta — awaiting approval (usually minutes to a few hours). You can send it once approved.',
          success: true,
        }));
        setRequestDialogOpen(false);
        setEditingTemplate(null);
        dispatch(getSearchWpTemplateList({ wpAccountId }));
        setPendingPollCount(6);
        // Route to pending filter so the user sees the re-submitted template with its status.
        setFilterStatus('PENDING');
      } else {
        const errMsg = res.data?.detail || res.data?.error || 'Failed to update template';
        dispatch(updateError({ message: typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg), success: false }));
      }
    } catch {
      dispatch(updateError({ message: 'Failed to update template', success: false }));
    } finally {
      setCreatingTemplate(false);
    }
  }, [canWriteProvider, wpAccountId, practiceId]);

  const handleTemplateSubmit = useCallback(async (payload) => {
    if (payload.isEdit) {
      await handleEditTemplate(payload);
    } else {
      await handleCreateTemplate(payload);
    }
  }, [handleCreateTemplate, handleEditTemplate]);

  useEffect(() => {
    if (practiceId) dispatch(getWhatsappAccount(practiceId));
  }, [practiceId]);

  useEffect(() => {
    if (wpAccountId) dispatch(getSearchWpTemplateList({ wpAccountId }));
  }, [wpAccountId]);

  const filtered = useMemo(() => {
    const q = String(search || '').toLowerCase();
    return templates.filter((t) => {
      if (q) {
        const bodyText = String((t.components || []).map(c => c.text || '').join(' ')).toLowerCase();
        if (!(
          String(t.name || '').toLowerCase().includes(q) ||
          String(t.language || '').toLowerCase().includes(q) ||
          String(t.status || '').toLowerCase().includes(q) ||
          String(t.category || '').toLowerCase().includes(q) ||
          bodyText.includes(q)
        )) return false;
      }
      if (filterAudience) {
        const summary = audienceSummary(t);
        if (filterAudience === 'restricted' && !summary.restricted) return false;
        if (filterAudience === 'everyone' && summary.restricted) return false;
        if (filterAudience === 'inherit' && !summary.inherited) return false;
      }
      if (filterUsageType && String(t.usage_type) !== String(filterUsageType)) return false;
      if (filterStatus && String(t.status) !== String(filterStatus)) return false;
      if (filterCategory && String(t.category) !== String(filterCategory)) return false;
      return true;
    });
  }, [templates, search, filterUsageType, filterStatus, filterCategory, filterAudience]);

  const statusOptions = useMemo(() => [...new Set(templates.map((t) => t.status).filter(Boolean))].sort(), [templates]);
  const categoryOptions = useMemo(() => [...new Set(templates.map((t) => t.category).filter(Boolean))].sort(), [templates]);

  const handleUsageTypeChange = useCallback(async (row, newUsageType) => {
    if (!canWriteProvider || !wpAccountId) return;
    const oldUsageType = row.usage_type;
    setPatchingId(row.id);
    dispatch(updateSingleUsageType({ id: row.id, usageType: newUsageType }));
    try {
      const res = await callApi(
        `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${wpAccountId}/whatsapp-message-templates/${row.id}/`,
        'PATCH',
        { usage_type: newUsageType }
      );
      if (!res.ok) {
        dispatch(updateSingleUsageType({ id: row.id, usageType: oldUsageType }));
        dispatch(updateError({ message: 'Failed to update usage type', success: false }));
      } else {
        dispatch(updateError({ message: 'Usage type updated', success: true }));
      }
    } catch {
      dispatch(updateSingleUsageType({ id: row.id, usageType: oldUsageType }));
      dispatch(updateError({ message: 'Failed to update usage type', success: false }));
    } finally {
      setPatchingId(null);
    }
  }, [canWriteProvider, wpAccountId, practiceId]);

  const handleBulkUsageTypeApply = useCallback(async () => {
    if (!canWriteProvider || !selectedRowIds.length || !bulkUsageType || !wpAccountId) return;
    const selectedTemplates = templates.filter((t) => selectedRowIds.includes(t.id));
    if (!selectedTemplates.length) return;
    setBulkPatching(true);
    const oldValues = {};
    selectedTemplates.forEach((t) => { oldValues[t.id] = t.usage_type; });
    dispatch(bulkUpdateUsageType({ ids: selectedRowIds, usageType: bulkUsageType }));
    try {
      const BATCH = 5;
      const failedIds = [];
      for (let i = 0; i < selectedTemplates.length; i += BATCH) {
        const batch = selectedTemplates.slice(i, i + BATCH);
        const results = await Promise.allSettled(
          batch.map((t) => callApi(
            `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${wpAccountId}/whatsapp-message-templates/${t.id}/`,
            'PATCH',
            { usage_type: bulkUsageType }
          ))
        );
        results.forEach((r, idx) => {
          if (r.status === 'rejected' || (r.status === 'fulfilled' && !r.value.ok)) {
            failedIds.push(batch[idx].id);
          }
        });
      }
      if (failedIds.length > 0) {
        dispatch(revertUsageTypes(Object.fromEntries(failedIds.map(id => [id, oldValues[id]]))));
        dispatch(updateError({ message: `${failedIds.length} of ${selectedTemplates.length} updates failed`, success: false }));
      } else {
        dispatch(updateError({ message: `Updated ${selectedTemplates.length} templates`, success: true }));
      }
    } catch {
      dispatch(revertUsageTypes(oldValues));
      dispatch(updateError({ message: 'Bulk update failed', success: false }));
    } finally {
      setBulkPatching(false);
      setSelectedRowIds([]);
      setBulkUsageType('');
    }
  }, [canWriteProvider, selectedRowIds, bulkUsageType, wpAccountId, practiceId]);

  const handleUpdateContextMap = useCallback((newContext, id) => {
    if (canWriteProvider && wpAccountId) {
      dispatch(updateWpContextMap({ wpAccountId, context: newContext, id }));
    }
  }, [canWriteProvider, wpAccountId]);

  // Single-template audience save, called from the preview drawer.
  const handleUpdateAudience = useCallback(async (payload, id) => {
    if (!canWriteProvider || !wpAccountId || !id) return;
    const res = await callApi(
      `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${wpAccountId}/whatsapp-message-templates/${id}/`,
      'PATCH',
      payload,
    );
    if (res?.status === 200) {
      dispatch(updateError({ message: 'Audience updated', success: true }));
      dispatch(getSearchWpTemplateList({ wpAccountId }));
      setPreviewTemplate((prev) => (prev && prev.id === id ? { ...prev, ...payload } : prev));
    } else {
      dispatch(updateError({
        message: res?.data ? JSON.stringify(res.data) : 'Could not update audience',
        success: false,
        status: res?.status,
      }));
    }
  }, [canWriteProvider, wpAccountId, practiceId]);

  // Bulk audience save. One request for the whole selection — unlike the
  // usage-type bulk action, which predates the bulk endpoint and still fans out
  // batched PATCHes with optimistic revert.
  const handleBulkAudienceApply = useCallback(async () => {
    if (!canWriteProvider || !wpAccountId || !selectedRowIds.length) return;
    if (validateRule(bulkAudienceDraft)) return;
    setBulkAudienceSaving(true);
    try {
      const res = await callApi(
        `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${wpAccountId}/whatsapp-message-templates/bulk-audience/`,
        'POST',
        { template_ids: selectedRowIds, ...toPayload(bulkAudienceDraft) },
      );
      if (res?.status === 200) {
        const updated = res?.data?.updated ?? selectedRowIds.length;
        dispatch(updateError({
          message: `Audience updated on ${updated} template${updated === 1 ? '' : 's'}`,
          success: true,
        }));
        dispatch(getSearchWpTemplateList({ wpAccountId }));
        setBulkAudienceOpen(false);
        setSelectedRowIds([]);
      } else {
        dispatch(updateError({
          message: res?.data ? JSON.stringify(res.data) : 'Bulk audience update failed',
          success: false,
          status: res?.status,
        }));
      }
    } finally {
      setBulkAudienceSaving(false);
    }
  }, [canWriteProvider, wpAccountId, practiceId, selectedRowIds, bulkAudienceDraft]);

  const columns = useMemo(() => [
    {
      field: 'name', headerName: 'Name', flex: 2, minWidth: 240,
      renderCell: (params) => (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: 'success.lighter', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Iconify icon="mdi:whatsapp" width={18} sx={{ color: 'success.main' }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 500 }} noWrap>{formatUsageLabel(params.row.name)}</Typography>
            <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', fontFamily: 'monospace' }} noWrap>{params.row.name}</Typography>
          </Box>
        </Stack>
      ),
    },
    {
      field: 'status', headerName: 'Status', flex: 1.2, minWidth: 150,
      renderCell: (p) => {
        const statusColors = {
          APPROVED: 'success.main',
          PENDING: 'warning.main',
          REJECTED: 'error.main',
          PAUSED: 'warning.dark',
          IN_APPEAL: 'warning.dark',
          DISABLED: 'error.dark',
          DELETED: 'error.dark',
          PENDING_DELETION: 'error.dark',
          LIMIT_EXCEEDED: 'warning.dark',
          FLAGGED: 'error.dark',
        };
        // Statuses that mean the template is throttled/blocked and reception
        // should know without opening the row.
        const WARNING_STATUSES = ['PAUSED', 'LIMIT_EXCEEDED', 'FLAGGED', 'DISABLED', 'IN_APPEAL'];
        const color = statusColors[p.value] || 'text.disabled';
        const isWarn = WARNING_STATUSES.includes(p.value);

        // Quality score dot — RED/YELLOW means Meta is throttling sends.
        const quality = (p.row.quality_score || '').toUpperCase();
        const QUALITY_COLORS = { RED: 'error.main', YELLOW: 'warning.main' };
        const qualityColor = QUALITY_COLORS[quality];
        const QUALITY_TOOLTIPS = {
          RED: 'Template quality: red. Meta is heavily throttling this template — replace it soon.',
          YELLOW: 'Template quality: yellow. Meta may throttle this template if quality drops further.',
        };
        const qualityTooltip = QUALITY_TOOLTIPS[quality] || null;

        // Reason text (Meta-provided) or fallback rejected_reason carried by older payloads.
        const reasonText = p.row.status_reason || p.row.rejected_reason || '';
        const updatedHint = p.row.status_updated_at
          ? `\nLast update: ${new Date(p.row.status_updated_at).toLocaleString()}`
          : '';

        const chipInner = (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: 'text.primary' }}>{p.value || '--'}</Typography>
            {qualityColor && (
              <Tooltip title={qualityTooltip || ''} placement="top">
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: qualityColor,
                    flexShrink: 0,
                    boxShadow: '0 0 0 2px rgba(255,255,255,0.9)',
                    ml: 0.25,
                  }}
                />
              </Tooltip>
            )}
            {isWarn && (
              <Tooltip
                title={
                  reasonText
                    ? `${reasonText}${updatedHint}`
                    : `Template state: ${p.value}.${updatedHint}`
                }
                placement="top"
              >
                <Box sx={{ display: 'inline-flex', color: 'warning.dark', ml: 0.25 }}>
                  <Iconify icon="mdi:alert-circle-outline" width={16} />
                </Box>
              </Tooltip>
            )}
          </Stack>
        );

        // If we have a reason and the row is in a non-warn state (e.g. REJECTED),
        // still surface it via the wrapping tooltip — preserves prior behaviour.
        if (!isWarn && reasonText) {
          return <Tooltip title={`${reasonText}${updatedHint}`} placement="top">{chipInner}</Tooltip>;
        }
        return chipInner;
      },
    },
    {
      field: 'category', headerName: 'Category', flex: 1, minWidth: 110,
      renderCell: (p) => {
        const cc = { AUTHENTICATION: { bg: 'info.lighter', color: 'info.darker' }, MARKETING: { bg: 'secondary.lighter', color: 'secondary.darker' }, UTILITY: { bg: 'warning.lighter', color: 'warning.darker' } };
        const c = cc[p.value] || { bg: 'grey.100', color: 'text.secondary' };
        return <Chip size="small" label={p.value || '--'} sx={{ bgcolor: c.bg, color: c.color, fontWeight: 500, fontSize: '0.75rem', borderRadius: 1.5 }} />;
      },
    },
    {
      field: 'usage_type', headerName: 'Usage Type', flex: 1.5, minWidth: 180,
      renderCell: (params) => (
        <TextField
          select
          size="small"
          value={params.row.usage_type || ''}
          onChange={(e) => handleUsageTypeChange(params.row, e.target.value)}
          disabled={!canWriteProvider || patchingId === params.row.id}
          fullWidth
          SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 300 } } } }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.8rem' }, '& .MuiSelect-select': { py: 0.75 } }}
        >
          <MenuItem value=""><em>None</em></MenuItem>
          {usageTypes.map((ut) => (<MenuItem key={ut} value={ut}>{formatUsageLabel(ut)}</MenuItem>))}
        </TextField>
      ),
    },
    ...(canViewAudience ? [{
      field: '_audience',
      headerName: 'Audience',
      flex: 1.2,
      minWidth: 150,
      sortable: false,
      // Read-only at a glance; the drawer is where it gets changed. Keeping the
      // grid cell non-interactive avoids a second, subtly different editor.
      renderCell: (params) => {
        const summary = audienceSummary(params.row);
        // Chip stays short for the grid; the tooltip carries the whole rule in
        // words, including which treatments, so nobody has to open the drawer
        // just to find out what a "2 treatments blocked" chip means.
        const detail = [
          audienceDescription(effectiveRule(params.row), { treatmentNameById: treatmentNames }),
          summary.inherited ? 'Follows the practice default.' : 'Set on this template.',
        ].filter(Boolean).join(' ');
        return (
          <Tooltip title={detail}>
            <Chip
              size="small"
              variant={summary.restricted ? 'filled' : 'outlined'}
              icon={<Iconify icon={summary.restricted ? 'mdi:filter-outline' : 'mdi:account-group-outline'} width={14} />}
              label={summary.label}
              sx={{
                maxWidth: '100%',
                fontSize: '0.7rem',
                fontWeight: summary.restricted ? 600 : 400,
                borderRadius: 1.5,
                ...(summary.restricted
                  ? { bgcolor: 'warning.lighter', color: 'warning.darker' }
                  : { color: 'text.disabled' }),
              }}
            />
          </Tooltip>
        );
      },
    }] : []),
    {
      field: 'updated_at_human', headerName: 'Updated', flex: 1, minWidth: 130,
      renderCell: (p) => (
        <Tooltip title={p.row.updated_at || ''} placement="top">
          <Typography variant="caption" color="text.secondary">{p.value || '--'}</Typography>
        </Tooltip>
      ),
    },
    {
      field: '_actions', headerName: '', width: 160, sortable: false, filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Preview template">
            <IconButton size="small" onClick={() => setPreviewTemplate(params.row)}>
              <Iconify icon="mdi:eye-outline" width={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title={canWriteProvider
            ? 'Edit template (once per 24 hours)'
            : 'Read-only history: reconnect this WhatsApp account to edit templates'}>
            <span>
              <IconButton
                size="small"
                aria-label="Edit template"
                title={canWriteProvider ? undefined : 'Read-only history: reconnect this WhatsApp account to edit templates'}
                disabled={!canWriteProvider}
                onClick={() => {
                  const confirmed = window.confirm(
                    'Editing a template submits it to Meta for re-approval (once per 24 hours). The template may be temporarily unavailable. Continue?'
                  );
                  if (confirmed) {
                    setEditingTemplate(params.row);
                    setRequestDialogOpen(true);
                  }
                }}
              >
                <Iconify icon="mdi:pencil-outline" width={18} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={canWriteProvider
            ? 'Duplicate template'
            : 'Read-only history: reconnect this WhatsApp account to duplicate templates'}>
            <span>
              <IconButton
                size="small"
                aria-label="Duplicate template"
                title={canWriteProvider ? undefined : 'Read-only history: reconnect this WhatsApp account to duplicate templates'}
                disabled={!canWriteProvider}
                onClick={() => handleCloneTemplate(params.row)}
              >
                <Iconify icon="mdi:content-copy" width={18} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={canWriteProvider
            ? 'Delete template'
            : 'Read-only history: reconnect this WhatsApp account to delete templates'}>
            <span>
              <IconButton
                size="small"
                aria-label="Delete template"
                onClick={() => {
                  if (window.confirm(`Delete template "${params.row.name}"? This cannot be undone.`)) {
                    handleDeleteTemplate(params.row);
                  }
                }}
                disabled={!canWriteProvider || deletingId === params.row.id}
              >
                <Iconify icon={deletingId === params.row.id ? 'mdi:loading' : 'mdi:delete-outline'} width={18} sx={{ color: deletingId === params.row.id ? 'text.disabled' : 'error.main' }} />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      ),
    },
  ], [usageTypes, patchingId, handleUsageTypeChange, deletingId, handleDeleteTemplate, handleCloneTemplate, canWriteProvider, treatmentNames, canViewAudience]);

  return (
    <Card sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', borderRadius: '16px', boxShadow: APPLE.shadow }}>
      <TemplateFilterToolbar
        search={search}
        onSearchChange={setSearch}
        filters={[
          { label: 'Status', value: filterStatus, onChange: setFilterStatus, options: statusOptions },
          { label: 'Category', value: filterCategory, onChange: setFilterCategory, options: categoryOptions },
          { label: 'Usage Type', value: filterUsageType, onChange: setFilterUsageType, options: usageTypes },
          ...(canViewAudience ? [{
            label: 'Audience',
            value: filterAudience,
            onChange: setFilterAudience,
            options: [
              { value: 'restricted', label: 'Restricted' },
              { value: 'everyone', label: 'Everyone' },
              { value: 'inherit', label: 'Practice default' },
            ],
          }] : []),
        ]}
        selectedCount={selectedRowIds.length}
        extraActions={
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleSyncTemplates}
              disabled={!canWriteProvider || syncing || !wpAccountId}
              startIcon={syncing ? <CircularProgress size={16} /> : <Iconify icon="mdi:sync" width={18} />}
              sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', whiteSpace: 'nowrap' }}
            >
              {syncing ? 'Syncing...' : 'Sync Templates'}
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => setRequestDialogOpen(true)}
              disabled={!canWriteProvider}
              startIcon={<Iconify icon="mdi:plus" width={18} />}
              sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', whiteSpace: 'nowrap' }}
            >
              Request Template
            </Button>
          </Stack>
        }
        bulkActions={[
          {
            label: 'Set Usage Type',
            icon: 'mdi:tag-multiple-outline',
            onClick: handleBulkUsageTypeApply,
            disabled: !canWriteProvider || !bulkUsageType || bulkPatching,
            loading: bulkPatching,
            selectComponent: (
              <TextField
                select
                size="small"
                value={bulkUsageType}
                onChange={(e) => setBulkUsageType(e.target.value)}
                disabled={!canWriteProvider}
                sx={{ minWidth: 160, '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.8rem', bgcolor: 'background.paper' } }}
                SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 300 } } } }}
              >
                <MenuItem value=""><em>Select type</em></MenuItem>
                {usageTypes.map((ut) => (<MenuItem key={ut} value={ut}>{formatUsageLabel(ut)}</MenuItem>))}
              </TextField>
            ),
          },
          ...(canManageAudience ? [{
            label: 'Set Audience',
            icon: 'mdi:account-filter-outline',
            onClick: () => setBulkAudienceOpen(true),
            disabled: !canWriteProvider || bulkAudienceSaving,
          }] : []),
        ]}
      />

      {templates.some((t) => t.status === 'PENDING') && (
        <Box sx={{ mx: 2, mb: 1, px: 2, py: 1, bgcolor: 'warning.lighter', borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Iconify icon="mdi:clock-outline" width={18} sx={{ color: 'warning.main' }} />
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: 'warning.darker' }}>
              {templates.filter((t) => t.status === 'PENDING').length} template(s) pending Meta approval — status updates automatically
            </Typography>
          </Stack>
          <Button
            size="small"
            onClick={handleSyncTemplates}
            disabled={!canWriteProvider || syncing}
            startIcon={<Iconify icon="mdi:refresh" width={16} />}
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}
          >
            Refresh now
          </Button>
        </Box>
      )}

      <Box sx={{ flex: 1, width: '100%', minHeight: 400, px: 2, pb: 2 }}>
        <DataGridPro
          rows={filtered}
          columns={columns}
          loading={loadingForWpTemplate}
          checkboxSelection={canWriteProvider}
          disableRowSelectionOnClick
          rowSelectionModel={selectedRowIds}
          onRowSelectionModelChange={(ids) => {
            if (canWriteProvider) setSelectedRowIds(ids);
          }}
          getRowId={(row) => row.id}
          getRowHeight={() => 'auto'}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[10, 25, 50]}
          pagination
          density="comfortable"
          onRowClick={(params, event) => {
            if (event.target.closest('button, [role="button"], .MuiIconButton-root, .MuiSelect-select, .MuiMenuItem-root, input, .MuiSwitch-root')) return;
            setPreviewTemplate(params.row);
          }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': { bgcolor: 'grey.50', borderRadius: 1.5 },
            '& .MuiDataGrid-row:hover': { bgcolor: 'action.hover' },
            '& .MuiDataGrid-cell': { borderBottom: `1px solid ${theme.palette.divider}`, py: 1 },
          }}
          slots={{
            noRowsOverlay: () => (
              <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', py: 6 }}>
                <Iconify icon="mdi:message-text-outline" width={48} sx={{ color: 'text.disabled', mb: 2 }} />
                <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                  No templates yet
                </Typography>
                <Typography sx={{ fontSize: '0.82rem', color: 'text.disabled', mb: 2 }}>
                  Create your first WhatsApp message template
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => setRequestDialogOpen(true)}
                  disabled={!canWriteProvider}
                  startIcon={<Iconify icon="mdi:plus" width={18} />}
                  sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' }}
                >
                  Create Template
                </Button>
              </Stack>
            ),
          }}
          slotProps={{
            loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' },
          }}
        />
      </Box>

      {canManageAudience && (
      <Dialog
        open={bulkAudienceOpen}
        onClose={() => !bulkAudienceSaving && setBulkAudienceOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ fontFamily: APPLE.font, fontWeight: 600, fontSize: '1.05rem', pb: 0.5 }}>
          Set audience on {selectedRowIds.length} template{selectedRowIds.length === 1 ? '' : 's'}
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 400, mt: 0.25, fontFamily: APPLE.font }}>
            This replaces whatever audience each one has now. Pick Practice
            default to clear their own rules and follow the practice.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {/* Exactly which templates are about to be overwritten. A bulk
              action that silently rewrites 18 rows should show its work. */}
          <Box sx={{ mb: 2 }}>
            <Typography sx={{
              fontSize: '0.68rem', fontWeight: 600, color: 'text.disabled',
              textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.75, fontFamily: APPLE.font,
            }}>
              Being changed
            </Typography>
            <Box sx={{
              maxHeight: 132,
              overflow: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '10px',
              px: 1.25,
              py: 0.75,
              bgcolor: 'background.neutral',
            }}>
              {templates
                .filter((t) => selectedRowIds.includes(t.id))
                .map((t) => {
                  const summary = audienceSummary(t);
                  return (
                    <Stack
                      key={t.id}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={1}
                      sx={{ py: 0.35 }}
                    >
                      <Typography noWrap sx={{ fontSize: '0.76rem', fontFamily: APPLE.font, minWidth: 0 }}>
                        {formatUsageLabel(t.name)}
                      </Typography>
                      {/* Current audience, so a rule that is about to be
                          discarded is visible before it is discarded. */}
                      <Typography sx={{
                        fontSize: '0.68rem',
                        color: summary.restricted ? 'warning.darker' : 'text.disabled',
                        flexShrink: 0,
                        fontFamily: APPLE.font,
                      }}>
                        {summary.label}
                      </Typography>
                    </Stack>
                  );
                })}
            </Box>
          </Box>

          <WhatsAppAudienceRulesEditor
            value={bulkAudienceDraft}
            onChange={setBulkAudienceDraft}
            practiceTreatments={practiceTreatments}
            disabled={bulkAudienceSaving}
            dense
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setBulkAudienceOpen(false)}
            disabled={bulkAudienceSaving}
            sx={{ textTransform: 'none', fontSize: '0.8rem' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleBulkAudienceApply}
            disabled={bulkAudienceSaving || !selectedRowIds.length || Boolean(validateRule(bulkAudienceDraft))}
            sx={{ textTransform: 'none', fontSize: '0.8rem', fontWeight: 600, borderRadius: 1.5 }}
          >
            {bulkAudienceSaving
              ? 'Applying…'
              : `Apply to ${selectedRowIds.length} template${selectedRowIds.length === 1 ? '' : 's'}`}
          </Button>
        </DialogActions>
      </Dialog>
      )}

      <TemplatePreviewDrawer
        open={!!previewTemplate}
        onClose={() => setPreviewTemplate(null)}
        template={previewTemplate}
        type="whatsapp"
        onUpdateContextMap={canWriteProvider ? handleUpdateContextMap : undefined}
        showAudience={canViewAudience}
        onUpdateAudience={canWriteProvider && canManageAudience ? handleUpdateAudience : undefined}
        practiceTreatments={practiceTreatments}
        onEdit={canWriteProvider ? (t) => {
          const confirmed = window.confirm(
            'Editing a template submits it to Meta for re-approval (once per 24 hours). The template may be temporarily unavailable. Continue?'
          );
          if (confirmed) {
            setPreviewTemplate(null);
            setEditingTemplate(t);
            setRequestDialogOpen(true);
          }
        } : undefined}
      />

      <RequestTemplateDialog
        open={requestDialogOpen}
        onClose={() => {
          setRequestDialogOpen(false);
          setEditingTemplate(null);
          setCloneData(null);
        }}
        onSubmit={handleTemplateSubmit}
        submitting={creatingTemplate}
        editTemplate={editingTemplate}
        onUpdateAudience={canWriteProvider && canManageAudience ? handleUpdateAudience : undefined}
        practiceTreatments={practiceTreatments}
        existingTemplateNames={existingNames}
        cloneData={cloneData}
      />
    </Card>
  );
};

WhatsappTemplateList.propTypes = {
  initialSearch: PropTypes.string,
};

export default WhatsappTemplateList;
